import type { ApprovalRequestHistoryPayload } from "@shared/services/salesforce/types";

export const NO_VALUE_PLACEHOLDER = "–";

export const COMMENT_COLLAPSE_THRESHOLD = 120;

export interface PendingRequestChange {
  countryCode: string;
  /** Not narrowed to the known union: Salesforce owns this value, and an unrecognised one still describes a real change. */
  fieldName: string;
  oldValue: string;
  newValue: string;
}

export const FIELD_LABEL_KEYS = {
  Sale_Price: "views.productsTable.requests.modal.fields.salePrice",
  Purchase_Price: "views.productsTable.requests.modal.fields.purchasePrice",
  webStatus: "views.productsTable.requests.modal.fields.status",
  weeklyInventoryCount: "views.productsTable.requests.modal.fields.weeklyInventory",
  intensityZone: "views.productsTable.requests.modal.fields.intensity",
  intensityPosition: "views.productsTable.requests.modal.fields.position",
  character: "views.productsTable.requests.modal.fields.character",
  badges: "views.productsTable.requests.modal.fields.badges",
} as const satisfies Record<ApprovalRequestHistoryPayload["fieldName"], string>;

type KnownFieldName = keyof typeof FIELD_LABEL_KEYS;
type FieldLabelKey = (typeof FIELD_LABEL_KEYS)[KnownFieldName];

/** Translation key for a known field, or undefined so callers can fall back to the raw name. */
export const getFieldLabelKey = (fieldName: string): FieldLabelKey | undefined => FIELD_LABEL_KEYS[fieldName as KnownFieldName];

const MODAL_KEY_PREFIX = "views.productsTable.requests.modal";

export const ACTION_TYPE_LABEL_KEYS = {
  "Status Change": `${MODAL_KEY_PREFIX}.actionTypes.statusChange`,
  "Price Update": `${MODAL_KEY_PREFIX}.actionTypes.priceUpdate`,
  "Inventory Update": `${MODAL_KEY_PREFIX}.actionTypes.inventoryUpdate`,
  "Attribute Update": `${MODAL_KEY_PREFIX}.actionTypes.attributeUpdate`,
  "Admin Attribute Update": `${MODAL_KEY_PREFIX}.actionTypes.adminAttributeUpdate`,
  "Admin Status Change": `${MODAL_KEY_PREFIX}.actionTypes.adminStatusChange`,
} as const;

export const APPROVAL_STATUS_LABEL_KEYS = {
  "Submitted for Approval": `${MODAL_KEY_PREFIX}.approvalStatuses.submittedForApproval`,
  Approved: `${MODAL_KEY_PREFIX}.approvalStatuses.approved`,
  Rejected: `${MODAL_KEY_PREFIX}.approvalStatuses.rejected`,
} as const;

export const STATUS_VALUE_LABEL_KEYS = {
  Publish: `${MODAL_KEY_PREFIX}.statusValues.publish`,
  Unpublish: `${MODAL_KEY_PREFIX}.statusValues.unpublish`,
  OutOfStock: `${MODAL_KEY_PREFIX}.statusValues.outOfStock`,
} as const;

const WEB_STATUS_FIELD_NAME = "webStatus";

type LabelKeyMap = Readonly<Record<string, string>>;

const lookupKey = <M extends LabelKeyMap>(keys: M, value: string): M[keyof M] | undefined =>
  Object.hasOwn(keys, value) ? keys[value as keyof M] : undefined;

/** Translation key for a Salesforce action type, or undefined so callers can show the raw value. */
export const getActionTypeLabelKey = (actionType: string) => lookupKey(ACTION_TYPE_LABEL_KEYS, actionType);

/** Translation key for a Salesforce approval status, or undefined so callers can show the raw value. */
export const getApprovalStatusLabelKey = (status: string) => lookupKey(APPROVAL_STATUS_LABEL_KEYS, status);

/** Only web-status values are translated; prices and counts are shown as-is. Unknown values fall back to raw. */
export const getChangeValueLabelKey = (fieldName: string, value: string) =>
  fieldName === WEB_STATUS_FIELD_NAME ? lookupKey(STATUS_VALUE_LABEL_KEYS, value) : undefined;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

/** Salesforce field casing isn't guaranteed to match the TypeScript interface, so match keys case-insensitively. */
const readKey = (record: Record<string, unknown>, key: string): unknown => {
  if (key in record) return record[key];
  const match = Object.keys(record).find((candidate) => candidate.toLowerCase() === key.toLowerCase());
  return match === undefined ? undefined : record[match];
};

/** A missing value is real — a first-time price has no previous value — so it shows as the placeholder, not as a dropped row. */
const toDisplayValue = (value: unknown): string | undefined => {
  if (value === null || value === undefined || value === "") return NO_VALUE_PLACEHOLDER;
  // Badges are recorded as a list.
  if (Array.isArray(value)) {
    const items = value.filter((item): item is string => typeof item === "string" && item !== "");
    return items.length === 0 ? NO_VALUE_PLACEHOLDER : items.join(", ");
  }
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean" ? String(value) : undefined;
};

const toChange = (countryCode: string, value: unknown): PendingRequestChange | undefined => {
  if (!isRecord(value)) return undefined;
  const fieldName = readKey(value, "fieldName");
  if (typeof fieldName !== "string" || !fieldName) return undefined;
  // Only an explicit false means "unchanged". A payload that omits the flag still describes a change,
  // and dropping those is why a whole request could render as a dash.
  const isUpdated = readKey(value, "isUpdated");
  if (isUpdated === false || isUpdated === "false") return undefined;
  const oldValue = toDisplayValue(readKey(value, "oldValue"));
  const newValue = toDisplayValue(readKey(value, "newValue"));
  if (oldValue === undefined || newValue === undefined) return undefined;
  return { countryCode, fieldName, oldValue, newValue };
};

const parseJson = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

/** Long-text fields can come back double-encoded, where one parse yields the JSON string rather than the value. */
const parsePayloadJson = (raw: string): unknown => {
  const parsed = parseJson(raw);
  return typeof parsed === "string" ? parseJson(parsed) : parsed;
};

/** One `{ countryCode: entries }` map, or a bare entry that was written without a country wrapper. */
const toChanges = (map: Record<string, unknown>): PendingRequestChange[] => {
  if (readKey(map, "fieldName") !== undefined) {
    const change = toChange("", map);
    return change ? [change] : [];
  }
  return Object.entries(map).flatMap(([countryCode, entries]) =>
    (Array.isArray(entries) ? entries : [entries]).map((entry) => toChange(countryCode, entry)).filter((change) => change !== undefined)
  );
};

/**
 * Flattens the per-country history payload into the changed fields only. Empty or
 * unparseable input yields [].
 *
 * Deliberately permissive about the envelope — an array of country maps, a single
 * country map, or a bare entry all resolve — because Salesforce owns this payload's
 * shape and a stricter reader silently renders every change as a dash.
 */
export const parseHistoryPayload = (raw: string | null | undefined): PendingRequestChange[] => {
  if (!raw) return [];
  const parsed = parsePayloadJson(raw);
  const countryMaps: readonly unknown[] = Array.isArray(parsed) ? parsed : [parsed];
  return countryMaps.filter(isRecord).flatMap(toChanges);
};

export const hasMultipleCountries = (changes: PendingRequestChange[]): boolean =>
  new Set(changes.map((change) => change.countryCode).filter(Boolean)).size > 1;

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parses a `YYYY-MM-DD` date or ISO datetime; returns undefined when missing or invalid.
 * Date-only values are built as local calendar dates so they don't shift a day west of UTC.
 */
export const parseEffectiveDate = (raw: string | null | undefined): Date | undefined => {
  if (!raw) return undefined;
  const dateOnly = DATE_ONLY_PATTERN.exec(raw);
  if (!dateOnly) {
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }
  const [year, month, day] = [Number(dateOnly[1]), Number(dateOnly[2]), Number(dateOnly[3])];
  const date = new Date(year, month - 1, day);
  // The Date constructor rolls invalid days over (e.g. month 13), so confirm the parts round-trip
  const isRealDate = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  return isRealDate ? date : undefined;
};

export const isLongComment = (comment: string): boolean => comment.length > COMMENT_COLLAPSE_THRESHOLD || comment.split("\n").length > 3;

export const formatChangeLabel = (label: string, countryCode: string, showCountry: boolean): string =>
  showCountry && countryCode ? `${label} (${countryCode})` : label;

export const formatPendingRequestChange = (change: PendingRequestChange, label: string, showCountry: boolean): string =>
  `${formatChangeLabel(label, change.countryCode, showCountry)}: ${change.oldValue} → ${change.newValue}`;
